// GetConfigsForm.jsx
import React, { useState } from "react";
import axios from "axios";
import { useAuth } from "../../../contexts/AuthContext";
import ViewConfigs from "./ViewConfigs";
import EditConfigs from "./EditConfigs";

const GetConfigsForm = () => {
  const [loading, setLoading] = useState(false);
  const [configs, setConfigs] = useState(null);
  const [message, setMessage] = useState("");
  const [activeMode, setActiveMode] = useState("view");
  const [loginFilter, setLoginFilter] = useState("");
  const [targetType, setTargetType] = useState("individuals");
  const { apiBaseUrl } = useAuth();

  // 👇 اضافه کردن state برای track کردن موفقیت fetch
  const [hasFetchedSuccessfully, setHasFetchedSuccessfully] = useState(false);

  // State برای ویرایش
  const [updateType, setUpdateType] = useState("custom_logins");
  const [updateTarget, setUpdateTarget] = useState("individuals");
  const [selectedLogins, setSelectedLogins] = useState([]);
  const [isActiveChange, setIsActiveChange] = useState(null);
  const [riskConfigChanges, setRiskConfigChanges] = useState({});
  const [pipConfigChanges, setPipConfigChanges] = useState({});
  const [ttlConfigChanges, setTtlConfigChanges] = useState({});
  const [additionalFieldsChanges, setAdditionalFieldsChanges] = useState({});
  const [availableRiskFields, setAvailableRiskFields] = useState([]);
  const [availablePipFields, setAvailablePipFields] = useState([]);
  const [availableTtlFields, setAvailableTtlFields] = useState([]);
  const [availableAdditionalFields, setAvailableAdditionalFields] = useState([]);
  const [updateLoading, setUpdateLoading] = useState(false);

  const handleFetchConfigs = async () => {
    setLoading(true);
    setMessage("");
    setConfigs(null);
    resetEditForm();
    setHasFetchedSuccessfully(false); // 👈 reset کردن

    try {
      const response = await axios.post(
        `${apiBaseUrl}/api/admin/final/accounts/configs`,
        {
          target: targetType
        },
        {
          headers: {
            Accept: "application/json",
          },
        }
      );

      if (response.data.ok) {
        const loginConfigs = response.data.data?.data?.configurations;
        
        if (!loginConfigs || loginConfigs === "" || (Array.isArray(loginConfigs) && loginConfigs.length === 0)) {
          setMessage("هیچ تنظیماتی یافت نشد");
          setConfigs(null);
          setHasFetchedSuccessfully(true); // 👈 حتی اگر خالی باشه، موفق بوده
          return;
        }

        setConfigs(response.data.data);
        setMessage("تنظیمات با موفقیت دریافت شد");
        setHasFetchedSuccessfully(true); // 👈 موفق بوده

        if (response.data.data?.data?.configurations?.[0]) {
          const firstConfig = response.data.data.data.configurations[0];
          
          setAvailableRiskFields(Object.keys(firstConfig.risk_config || {}).sort());
          setAvailablePipFields(Object.keys(firstConfig.pip_config || {}).sort());
          setAvailableTtlFields(Object.keys(firstConfig.ttl_config || {}).sort());
          
          const excludedFields = ['risk_config', 'pip_config', 'ttl_config'];
          const additionalFields = Object.keys(firstConfig)
            .filter(key => !excludedFields.includes(key))
            .sort();
          
          setAvailableAdditionalFields(additionalFields);
        }
      }else{
           setMessage(response?.message || "خطا در دریافت تنظیمات")
        

      }
    } catch (error) {
      console.error("Error:", error);
      setMessage(error.response?.data?.message || "خطا در دریافت تنظیمات");
      setHasFetchedSuccessfully(true); // 👈 حتی با خطا، اجازه بده Edit رو ببینه
    } finally {
      setLoading(false);
    }
  };

  const resetEditForm = () => {
    setUpdateType("custom_logins");
    setUpdateTarget("individuals");
    setSelectedLogins([]);
    setIsActiveChange(null);
    setRiskConfigChanges({});
    setPipConfigChanges({});
    setTtlConfigChanges({});
    setAdditionalFieldsChanges({});
  };

  const handleUpdateConfigs = async () => {
    if (updateType === "custom_logins" && selectedLogins.length === 0) {
      setMessage("لطفاً حداقل یک Login انتخاب کنید یا نوع به‌روزرسانی دیگری را انتخاب کنید");
      return;
    }

    if (
      isActiveChange === null &&
      Object.keys(riskConfigChanges).length === 0 &&
      Object.keys(pipConfigChanges).length === 0 &&
      Object.keys(ttlConfigChanges).length === 0 &&
      Object.keys(additionalFieldsChanges).length === 0
    ) {
      setMessage("لطفاً حداقل یک تغییر وارد کنید");
      return;
    }

    setUpdateLoading(true);
    setMessage("");

    try {
      const payload = {
        type: updateType,
      };

      if (updateType === "custom_logins") {
        payload.target = updateTarget;
        payload.logins = selectedLogins;
      }

      if (isActiveChange !== null) {
        payload.is_active = isActiveChange;
      }

      if (Object.keys(riskConfigChanges).length > 0) {
        payload.risk_config = riskConfigChanges;
      }

      if (Object.keys(pipConfigChanges).length > 0) {
        payload.pip_config = pipConfigChanges;
      }

      if (Object.keys(ttlConfigChanges).length > 0) {
        payload.ttl_config = ttlConfigChanges;
      }

      if (Object.keys(additionalFieldsChanges).length > 0) {
        Object.entries(additionalFieldsChanges).forEach(([key, value]) => {
          payload[key] = value;
        });
      }

      const response = await axios.post(
        `${apiBaseUrl}/api/admin/final/accounts/update/configs`,
        payload,
        {
          headers: {
            "Content-Type": "application/json",
            "Accept": "application/json",
          },
        }
      );

      if (response.data.ok || response.data.success) {
        setMessage("تنظیمات با موفقیت به‌روزرسانی شد");
        resetEditForm();
        setActiveMode("view");
        handleFetchConfigs();
      }
    } catch (error) {
      console.error("Error:", error);
      setMessage(error.response?.data?.message || "خطا در به‌روزرسانی تنظیمات");
    } finally {
      setUpdateLoading(false);
    }
  };

  const toggleLoginSelection = (login) => {
    if (selectedLogins.includes(login)) {
      setSelectedLogins(selectedLogins.filter((l) => l !== login));
    } else {
      setSelectedLogins([...selectedLogins, login]);
    }
  };

  const filteredConfigs = configs?.data?.configurations && 
    Array.isArray(configs.data.configurations)
      ? configs.data.configurations.filter((config) =>
          config.login.includes(loginFilter) || 
          config.login_name?.toLowerCase().includes(loginFilter.toLowerCase())
        )
      : [];

  return (
    <div className="space-y-6">
      <div className="bg-gray-800 border border-gray-600 rounded-lg p-4">
        <label className="block text-white font-medium mb-3">
          انتخاب نوع حساب:
        </label>
        <div className="flex gap-4">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name="targetType"
              value="individuals"
              checked={targetType === "individuals"}
              onChange={(e) => setTargetType(e.target.value)}
              className="w-5 h-5 accent-blue-600"
            />
            <span className="text-white">Individuals (حساب‌های فردی)</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name="targetType"
              value="groups" 
              checked={targetType === "groups"}
              onChange={(e) => setTargetType(e.target.value)}
              className="w-5 h-5 accent-blue-600"
            />
            <span className="text-white">Groups (گروه‌ها)</span>
          </label>
        </div>
      </div>

      {message && (
        <div
          className={`p-4 rounded-lg text-center ${
            message.includes("موفقیت")
              ? "bg-green-900 border border-green-600 text-green-400"
              : message.includes("یافت نشد")
              ? "bg-yellow-900 border border-yellow-600 text-yellow-400"
              : "bg-red-900 border border-red-600 text-red-400"
          }`}
        >
          {message}
        </div>
      )}

      <button
        onClick={handleFetchConfigs}
        disabled={loading}
        className="w-full py-3 px-6 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 text-white font-medium rounded-lg transition duration-200 flex items-center justify-center gap-2"
      >
        {loading ? (
          <>
            <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
            در حال دریافت...
          </>
        ) : (
          `دریافت تنظیمات ${targetType === "individuals" ? "حساب‌های فردی" : "گروه‌ها"}`
        )}
      </button>

      {/* 👇 تغییر شرط: از hasFetchedSuccessfully استفاده می‌کنیم */}
      {hasFetchedSuccessfully && (
        <>
          <div className="flex gap-2 bg-gray-800 rounded-lg p-1">
            <button
              onClick={() => setActiveMode("view")}
              className={`flex-1 py-2 px-4 rounded-md transition duration-200 font-medium ${
                activeMode === "view"
                  ? "bg-blue-600 text-white"
                  : "text-gray-300 hover:text-white hover:bg-gray-700"
              }`}
            >
              مشاهده تنظیمات
            </button>
            <button
              onClick={() => {
                setActiveMode("edit");
                resetEditForm();
              }}
              className={`flex-1 py-2 px-4 rounded-md transition duration-200 font-medium ${
                activeMode === "edit"
                  ? "bg-yellow-600 text-white"
                  : "text-gray-300 hover:text-white hover:bg-gray-700"
              }`}
            >
              ویرایش تنظیمات
            </button>
          </div>

          {activeMode === "view" && (
            <>
              {/* 👇 فقط اگر configs وجود داشته باشه نمایش بده */}
              {configs?.data?.configurations && 
               Array.isArray(configs.data.configurations) && 
               configs.data.configurations.length > 0 ? (
                <>
                  <div className="bg-gray-800 border border-gray-600 rounded-lg p-4">
                    <label className="block text-white font-medium mb-2">
                      جستجو بر اساس Login یا نام:
                    </label>
                    <input
                      type="text"
                      value={loginFilter}
                      onChange={(e) => setLoginFilter(e.target.value)}
                      placeholder="Login یا نام را وارد کنید..."
                      className="w-full p-3 bg-gray-900 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:border-blue-500 focus:outline-none"
                    />
                    {loginFilter && (
                      <p className="text-gray-400 text-sm mt-2">
                        {filteredConfigs.length} نتیجه یافت شد
                      </p>
                    )}
                  </div>

                  <ViewConfigs configs={filteredConfigs} />
                </>
              ) : (
                <div className="bg-gray-800 border border-gray-600 rounded-lg p-8 text-center">
                  <p className="text-gray-400">
                    هیچ تنظیماتی برای نمایش وجود ندارد
                  </p>
                </div>
              )}
            </>
          )}

          {/* 👇 Edit mode همیشه نمایش داده میشه */}
          {activeMode === "edit" && (
            <EditConfigs
              configs={configs?.data?.configurations || []} // 👈 اگر null بود، array خالی بده
              updateType={updateType}
              setUpdateType={setUpdateType}
              updateTarget={updateTarget}
              setUpdateTarget={setUpdateTarget}
              selectedLogins={selectedLogins}
              toggleLoginSelection={toggleLoginSelection}
              isActiveChange={isActiveChange}
              setIsActiveChange={setIsActiveChange}
              availableRiskFields={availableRiskFields}
              availablePipFields={availablePipFields}
              availableTtlFields={availableTtlFields}
              riskConfigChanges={riskConfigChanges}
              setRiskConfigChanges={setRiskConfigChanges}
              pipConfigChanges={pipConfigChanges}
              setPipConfigChanges={setPipConfigChanges}
              ttlConfigChanges={ttlConfigChanges}
              setTtlConfigChanges={setTtlConfigChanges}
              additionalFieldsChanges={additionalFieldsChanges}
              setAdditionalFieldsChanges={setAdditionalFieldsChanges}
              availableAdditionalFields={availableAdditionalFields}
              handleUpdateConfigs={handleUpdateConfigs}
              resetEditForm={resetEditForm}
              updateLoading={updateLoading}
              setSelectedLogins={setSelectedLogins} // ✅ این خط رو اضافه کن
            />
          )}
        </>
      )}
    </div>
  );
};

export default GetConfigsForm;